import { inject, Injectable, TemplateRef } from '@angular/core';
import { NeoDialogRef } from '@neogov/pattern-library/neo-dialog';
import { ConfirmationProvider } from '@core';

/**
 * Configuration for auto-save confirmation dialog
 * Generic type T represents the pending change data structure
 */
export interface ConfirmationConfig<T> {
  /** Dialog title */
  title: string;
  /** Template reference for dialog content */
  contentTemplate: TemplateRef<unknown>;
  /** Data representing the pending change */
  pendingChangeData: T;
  /** Callback invoked when user confirms */
  onConfirm: (data: T) => void;
  /** Callback invoked when user cancels */
  onCancel: (data: T) => void;
  /** Automation ID for testing */
  autId: string;
  /** Dialog width (optional, defaults to 490px) */
  width?: string;
}

/**
 * Service that provides auto-save confirmation pattern
 * Wraps ConfirmationProvider to handle confirmation, cancellation, and close (X/ESC) scenarios
 *
 * Usage:
 * ```typescript
 * this._autoSaveConfirmation.openConfirmation({
 *   title: 'Confirm Status Change',
 *   contentTemplate: this.changeConfirmTemplate(),
 *   pendingChangeData: statusChange,
 *   onConfirm: (change) => this._saveStatusChange(change),
 *   onCancel: (change) => this._revertStatusChange(change),
 *   autId: 'confirm-status-change'
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AutoSaveConfirmationService {
  private readonly _confirmation = inject(ConfirmationProvider);

  /**
   * Opens a confirmation dialog for auto-save operations
   * Handles three scenarios:
   * 1. User clicks Confirm → onConfirm callback
   * 2. User clicks Cancel, closes via X, or presses ESC → onCancel callback (revert)
   *
   * @param config Configuration object with callbacks and pending change data
   * @returns NeoDialogRef for further customization if needed
   */
  openConfirmation<T>(config: ConfirmationConfig<T>): NeoDialogRef<unknown, string> {
    const dialogRef = this._confirmation.open({
      autId: config.autId,
      title: config.title,
      bodyTemplate: config.contentTemplate,
      width: config.width || '490px',
      confirm: {
        label: 'Confirm',
        value: 'confirm',
        autIdSuffix: 'confirm-btn',
        variant: 'flat',
        color: 'primary',
      },
    });

    // ESC/backdrop/X already resolve to 'cancel' — no separate close handling needed.
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        config.onConfirm(config.pendingChangeData);
      } else {
        config.onCancel(config.pendingChangeData);
      }
    });

    return dialogRef;
  }
}
